import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";

import { MenuItem } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useYupValidation from "Hooks/useYupValidation";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Select } from "../../../../../../../../../../../components/Form";
import { AppSwitch } from "../../../../../../../../../../../components/Generic/AppSwitch";
import Modal from "../../../../../../../../../../../components/Generic/Modal";
import SkeletonComponent from "../../../../../../../../../../../components/Generic/Skeleton";
import { Member } from "../../../../../../../../../../../models/members/member";
import SubcontractorService from "../../../../../../../../../../../services/subcontractor.service";
import { SubcontractorKeys } from "../../../../../../../../../../../util/queries";
import useStyles, { ModalSwitchLabel, SwitchWrapper } from "./styles";

interface Props {
  onSubmit: (member: Member) => void;
}

export interface ConfigureSubcontractorHandler {
  onOpenModal: (member: Member) => void;
  onCloseModal: () => void;
}

const ConfigureSubcontractorModal: ForwardRefRenderFunction<
  ConfigureSubcontractorHandler,
  Props
> = (props, ref) => {
  const { t: translate } = useTranslation();
  const { onSubmit } = props;
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [currentMember, setCurrentMember] = useState<Member>();
  const [belongsToASubcontractor, setBelongsToASubcontractor] = useState<boolean>(false);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const onOpenModal = (member: Member) => {
    setCurrentMember(member);
    setBelongsToASubcontractor(!!member?.subcontractorId || false);
    setErrors([]);
    setHasFormBeenSubmitted(false);
    setOpen(true);
  };

  const { data: subcontractorList, isFetching } = useQuery({
    queryKey: SubcontractorKeys.getAll([]),
    queryFn: async () => {
      const response = await SubcontractorService.getAll({
        companyId: currentMember?.companyId,
        isActive: 1,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!currentMember?.companyId && belongsToASubcontractor,
    refetchOnWindowFocus: false,
  });

  useImperativeHandle(ref, () => ({
    onOpenModal,
    onCloseModal: () => setOpen(false),
  }));

  const handleValidate = () => {
    try {
      const schema = yup.object().shape({
        subcontractorId: yup
          .number()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
      });

      schema.validateSync(
        {
          subcontractorId: currentMember?.subcontractorId,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleSubmit = (currentMember: Member) => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (handleValidate()) onSubmit(currentMember);
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      handleValidate();
    }
  }, [hasFormBeenSubmitted, currentMember]);

  return (
    <Modal
      title={translate("configureAsSubcontractor")}
      open={open}
      handleClose={() => setOpen(false)}
      showModalFooter
      showActionButtons
      actionButtonProps={{
        onCancel: () => setOpen(false),
        onSave: () => {
          if (!currentMember) return;
          if (belongsToASubcontractor) return handleSubmit(currentMember);

          return onSubmit(currentMember);
        },
        showSaveButton: true,
        showCancelButton: true,
        textSave: translate("save"),
      }}
      className={classes.modalWrapper}
    >
      <div>
        <SwitchWrapper>
          <AppSwitch
            checked={belongsToASubcontractor}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setBelongsToASubcontractor(event.target.checked);
              if (!event.target.checked) {
                setCurrentMember((prev) => ({
                  ...(prev || ({} as Member)),
                  subcontractorId: null,
                  subcontractor: null,
                }));
              }
            }}
          />
          <ModalSwitchLabel>{translate("thisMemberBelongsToASubcontractor")}</ModalSwitchLabel>
        </SwitchWrapper>
        {belongsToASubcontractor || currentMember?.subcontractorId ? (
          <SkeletonComponent isLoading={isFetching}>
            <Select
              label={translate("subcontractor")}
              required
              error={fieldHasError("subcontractorId")}
              errorMessage={getErrorByField("subcontractorId")}
              value={Number(currentMember?.subcontractorId)}
              onChange={(event) => {
                const subcontractor = subcontractorList?.find(
                  (sub) => Number(sub?.id) === Number(event.target.value)
                );

                setCurrentMember((prev) => ({
                  ...(prev || ({} as Member)),
                  subcontractorId: Number(event.target.value),
                  subcontractor,
                }));
              }}
              placeholder={
                subcontractorList?.length
                  ? translate("select")
                  : translate("noSubcontractorAvailable")
              }
            >
              {subcontractorList?.map((subcontractor) => {
                return (
                  <MenuItem key={subcontractor.id} value={subcontractor.id}>
                    {subcontractor?.businessName}
                  </MenuItem>
                );
              })}
            </Select>
          </SkeletonComponent>
        ) : null}
      </div>
    </Modal>
  );
};

export default forwardRef(ConfigureSubcontractorModal);
